import axios from 'axios';

import Attribution from '@web-solutions/module-attribution';

import { getEnvUrl } from './getEnvUrl';

/**
 * Stripe hosted-Checkout API client (A/B variant `useStripeCheckout`).
 * Endpoints share the base path of the current payments API (`/payments/...`).
 * See the "Checkout Stripe" spec for the contract.
 */

// payment_system_project — varies per deployment/domain, so it's read from the
// env (`REACT_APP_PAYMENT_SYSTEM_PROJECT`). Falls back to `ravelo`. survei.tech
// funnels reuse the same project value; there is no separate value for survei.
export const PAYMENT_SYSTEM_PROJECT =
  process.env.REACT_APP_PAYMENT_SYSTEM_PROJECT || 'ravelo';

// Paths Stripe redirects back to after the hosted checkout. The backend appends
// `?session_id={CHECKOUT_SESSION_ID}` to the success URL.
export const CHECKOUT_SUCCESS_PATH = '/checkout/success';
export const CHECKOUT_CANCEL_PATH = '/checkout/cancel';

// The app is served under a sub-path (PUBLIC_URL, e.g. `/ravelo` in prod,
// `/ravelo-stage` on stage, '' in local dev). Return URLs and the return
// detection must account for it. NB: the hosting/CDN must rewrite
// `<base>/checkout/*` to the app's index.html (devops).
const getAppBasePath = (): string => {
  const publicUrl = process.env.PUBLIC_URL || '';
  // PUBLIC_URL can be absolute (full URL) or relative ('/ravelo'); we only want
  // the path portion, without a trailing slash.
  try {
    const path = publicUrl.startsWith('http')
      ? new URL(publicUrl).pathname
      : publicUrl;
    return path.replace(/\/$/, '');
  } catch (_) {
    return publicUrl.replace(/\/$/, '');
  }
};

// Polling cadence for the post-redirect status check.
export const CHECKOUT_POLL_INTERVAL_MS = 2500;
export const CHECKOUT_POLL_TIMEOUT_MS = 90000;

// sessionStorage key holding the session_id read from the success URL, so the
// status page survives the URL being cleaned up after the return.
const CHECKOUT_SESSION_STORAGE_KEY = 'stripe_checkout_session_id';

// sessionStorage key holding the funnel-defining query string (e.g.
// `?flow=ethnicity`) captured before the Stripe redirect. The hosted Checkout
// round-trip drops our query params and the backend only re-adds `?session_id`,
// so without this the app would reload into the default flow on return.
const CHECKOUT_FUNNEL_SEARCH_KEY = 'stripe_checkout_funnel_search';

export type CheckoutSessionPayload = {
  payment_system_project: string;
  trial_payment_price_id: string;
  subscription_price_id: string;
  subscription_product_id?: string;
  product_code: string;
  trial_period_days?: string;
  success_url: string;
  cancel_url: string;
};

export type CheckoutSessionResponse = {
  session_id: string;
  session_url: string;
  payment_system_project: string;
};

export type CheckoutStatus = 'success' | 'pending' | 'failed' | 'in_progress';

export type CheckoutStatusResponse = {
  status: CheckoutStatus;
  requiresEmail?: boolean;
  // Present on `success` when the backend collected the email on Stripe. When
  // set, the frontend attaches the full onboarding/purchase data to the user via
  // update-by-token (same payload as the paywall card flow).
  email?: string | null;
};

/** Build the absolute success/cancel URLs for the current origin + app base path. */
export const getCheckoutReturnUrls = () => {
  const base = `${window.location.origin}${getAppBasePath()}`;
  return {
    success_url: `${base}${CHECKOUT_SUCCESS_PATH}`,
    cancel_url: `${base}${CHECKOUT_CANCEL_PATH}`,
  };
};

/** POST /payments/checkout/sessions — requires `Authorization: Bearer <token>`. */
export const createCheckoutSession = async (
  payload: CheckoutSessionPayload,
  accessToken: string
): Promise<CheckoutSessionResponse> => {
  const { apiBaseUrl } = getEnvUrl();

  // FB ad labels captured on the landing, so the purchase made on the hosted
  // Checkout page can be attributed to its campaign. Omitted entirely for an
  // organic visit.
  const attribution = Attribution.getAdAttribution();

  const { data } = await axios.post<CheckoutSessionResponse>(
    `${apiBaseUrl}/payments/checkout/sessions`,
    { ...payload, ...(attribution ? { attribution } : {}) },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  return data;
};

/**
 * GET /payments/checkout/sessions/{session_id}/status?project={project}
 * No authorization.
 */
export const getCheckoutSessionStatus = async (
  sessionId: string,
  project: string = PAYMENT_SYSTEM_PROJECT
): Promise<CheckoutStatusResponse> => {
  const { apiBaseUrl } = getEnvUrl();

  const { data } = await axios.get<CheckoutStatusResponse>(
    `${apiBaseUrl}/payments/checkout/sessions/${sessionId}/status`,
    { params: { project } }
  );

  return data;
};

/** POST /payments/checkout/manual-email — no authorization. */
export const submitCheckoutManualEmail = async (
  sessionId: string,
  email: string,
  project: string = PAYMENT_SYSTEM_PROJECT
): Promise<{ success: boolean }> => {
  const { apiBaseUrl } = getEnvUrl();

  const { data } = await axios.post<{ success: boolean }>(
    `${apiBaseUrl}/payments/checkout/manual-email`,
    { session_id: sessionId, email, project }
  );

  return data;
};

// --- Return-from-Stripe URL helpers -----------------------------------------

export type CheckoutReturnKind = 'success' | 'cancel' | null;

/** Inspect the current location for a Stripe Checkout return. */
export const detectCheckoutReturn = (): {
  kind: CheckoutReturnKind;
  sessionId: string | null;
} => {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  const sessionId = params.get('session_id');

  // Match regardless of the app base path (`/ravelo/checkout/success` etc.).
  if (pathname.includes(CHECKOUT_SUCCESS_PATH)) {
    return { kind: 'success', sessionId };
  }
  if (pathname.includes(CHECKOUT_CANCEL_PATH)) {
    return { kind: 'cancel', sessionId };
  }
  return { kind: null, sessionId: null };
};

export const storeCheckoutSessionId = (sessionId: string) => {
  try {
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_KEY, sessionId);
  } catch (_) {
    /* ignore storage errors */
  }
};

export const readCheckoutSessionId = (): string | null => {
  try {
    return sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_KEY);
  } catch (_) {
    return null;
  }
};

export const clearCheckoutSessionId = () => {
  try {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_KEY);
  } catch (_) {
    /* ignore storage errors */
  }
};

/**
 * Persist the current funnel query string before redirecting to Stripe, so the
 * flow (and any other entry params) can be restored when the user returns.
 * No-op when the current URL has no query.
 */
export const storeFunnelSearch = () => {
  try {
    if (window.location.search) {
      sessionStorage.setItem(CHECKOUT_FUNNEL_SEARCH_KEY, window.location.search);
    }
  } catch (_) {
    /* ignore storage errors */
  }
};

/** The persisted funnel query string (e.g. `?flow=ethnicity`), or '' if none. */
export const readFunnelSearch = (): string => {
  try {
    return sessionStorage.getItem(CHECKOUT_FUNNEL_SEARCH_KEY) || '';
  } catch (_) {
    return '';
  }
};

/** The persisted `flow` value, or null if none was stored. */
export const readFunnelFlow = (): string | null => {
  const search = readFunnelSearch();
  if (!search) return null;
  try {
    return new URLSearchParams(search).get('flow');
  } catch (_) {
    return null;
  }
};

/**
 * Replace the `/checkout/*` URL with the app root so a reload doesn't re-trigger.
 * Restores the original funnel query (e.g. `?flow=test1`) that was dropped on the
 * Stripe round-trip, so the address bar matches the URL the user entered through.
 */
export const cleanCheckoutUrl = () => {
  try {
    const search = readFunnelSearch();
    const root = `${window.location.origin}${getAppBasePath()}/${search}`;
    window.history.replaceState({}, document.title, root);
  } catch (_) {
    /* ignore history errors */
  }
};
